<template>
  <div class="band">
    <div class="cell">
      <h2 class="cap">Containers</h2>
      <div class="big" :class="onlineTone" :title="onlineHint">{{ s.up }}<small> / {{ s.total }} en ligne</small></div>
      <p class="sub">
        <span :class="alertTone">{{ integer(s.warning + s.critical) }} en alerte</span>
        · <span :title="`Dont ${integer(s.serverErrors)} erreur(s) serveur depuis leur démarrage`">{{ integer(s.requests) }} requêtes<span v-if="s.serverErrors" class="warn">&nbsp;({{ integer(s.serverErrors) }} erreurs)</span></span>
        · <span title="Espace de stockage cumulé des bases de données">bases {{ bytes(s.databaseBytes) }}</span>
      </p>
      <p v-if="s.down || s.unsupported" class="sub" :class="onlineTone">{{ onlineHint }}</p>
    </div>
    <div v-if="data.host" class="cell">
      <h2 class="cap">Machine hôte · {{ data.host.cores }} cœur(s)</h2>
      <div v-for="meter in meters" :key="meter.key" class="meter" :title="meter.hint">
        <span class="meter-label">{{ meter.label }}</span>
        <div class="track" role="progressbar" aria-valuemin="0" aria-valuemax="100" :aria-valuenow="Math.round(meter.percent)" :aria-label="meter.label"><i :class="meter.tone" :style="{width: `${Math.min(100, Math.max(0, meter.percent))}%`}"></i></div>
        <strong class="meter-text">{{ meter.text }}</strong>
      </div>
    </div>
    <div class="cell">
      <h2 class="cap" title="Somme des containers qui répondent ; les pics sont ceux de la période du graphique ci-dessous">Consommation des containers</h2>
      <div class="pair" :title="memoryPeakHint"><strong>{{ memoryNow }}</strong><span>mémoire · pic {{ memoryPeak }}</span></div>
      <div class="pair" :title="cpuPeakHint"><strong>{{ cpuNow }}</strong><span>CPU · pic {{ cpuPeak }}</span></div>
    </div>
  </div>
</template>

<script>
import {integer, percent} from "@/statistics/utils/format";
import {bytes, dayMinute, usageTone} from "@/administration/utils";

/**
 * Bandeau du haut de la page des containers : l'état des containers, la machine hôte (mémoire, charge, disques) et la consommation de tous les containers, avec leurs pics.
 * data : la vue d'ensemble renvoyée par la passerelle ; history : l'historique d'infrastructure (nul tant qu'il n'est pas chargé).
 */
export default {
  name: 'InfraBand',
  props: {
    data: {type: Object, required: true},
    history: {type: Object, default: null},
  },
  computed: {
    s() {
      return this.data.summary;
    },
    onlineTone() {
      return this.s.down ? 'bad' : this.s.unsupported ? 'warn' : '';
    },
    alertTone() {
      return this.s.critical ? 'bad' : this.s.warning ? 'warn' : '';
    },
    onlineHint() {
      if (this.s.down) return `${this.s.down} hors ligne`;
      if (this.s.unsupported) return `${this.s.unsupported} non supervisé(s) : à redéployer`;
      return 'Tous les containers répondent';
    },
    meters() {
      const host = this.data.host;
      const thresholds = this.data.thresholds;
      const used = host.memoryTotalBytes - host.memoryAvailableBytes;
      const memoryPercent = host.memoryTotalBytes ? used / host.memoryTotalBytes * 100 : 0;
      // Charge sur 1 minute rapportée au nombre de cœurs : 100 % = tous les cœurs occupés
      const loadPercent = host.cores ? host.loadAverage[0] / host.cores * 100 : 0;
      return [
        {key: 'memory', label: 'Mémoire', text: `${bytes(used)} / ${bytes(host.memoryTotalBytes)}`, percent: memoryPercent, hint: `${bytes(host.memoryAvailableBytes)} disponibles`,
          tone: usageTone(memoryPercent, {warning: 100 - thresholds.hostMemoryAvailablePercent.warning, critical: 100 - thresholds.hostMemoryAvailablePercent.critical})},
        {key: 'load', label: 'Charge', text: host.loadAverage.join(' · '), percent: loadPercent, hint: 'Charge du processeur sur 1 / 5 / 15 minutes', tone: usageTone(loadPercent, {warning: 80, critical: 100})},
        ...host.disks.map(disk => ({
          key: disk.filesystem + disk.mount, label: `Disque ${disk.mount}`, text: `${bytes(disk.usedBytes)} / ${bytes(disk.totalBytes)}`, percent: disk.usedPercent,
          hint: `${bytes(disk.availableBytes)} libres (${disk.filesystem})`, tone: usageTone(disk.usedPercent, thresholds.diskPercent),
        })),
      ];
    },
    // Le dernier point qui a une mesure
    memoryNow() {
      const value = this.history ? this.last(this.history.total.memoryBytes) : null;
      return value === null ? '—' : bytes(value);
    },
    cpuNow() {
      const value = this.history ? this.last(this.history.total.cpuPercent) : null;
      return value === null ? '—' : percent(value);
    },
    memoryPeak() {
      return this.history && this.history.peaks.memory ? bytes(this.history.peaks.memory.value) : '—';
    },
    cpuPeak() {
      return this.history && this.history.peaks.cpu ? percent(this.history.peaks.cpu.value) : '—';
    },
    memoryPeakHint() {
      return this.history && this.history.peaks.memory ? `Pic de mémoire le ${dayMinute(this.history.peaks.memory.at)}` : 'Aucune mesure sur la période';
    },
    cpuPeakHint() {
      return this.history && this.history.peaks.cpu ? `Pic de CPU le ${dayMinute(this.history.peaks.cpu.at)} (100 % = un cœur)` : 'Aucune mesure sur la période';
    },
  },
  methods: {
    integer, bytes,
    last(values) {
      for (let index = values.length - 1; index >= 0; index--) if (values[index] !== null) return values[index];
      return null;
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '~/src/common/style/common.styl';

.band
  display grid
  grid-template-columns minmax(0, 1.1fr) minmax(0, 1.6fr) minmax(0, 1.1fr)
  background white
  border 1px solid #e5e5e5
  border-radius 14px
  box-shadow 0 1px 3px rgba(0,0,0,.05)

  @media only screen and (max-width: DESKTOP_SIZE)
    grid-template-columns minmax(0, 1fr)

.cell
  min-width 0
  padding 12px 18px

  & + .cell
    border-left 1px solid #eceff5

    @media only screen and (max-width: DESKTOP_SIZE)
      border-left 0
      border-top 1px solid #eceff5

.cap
  margin 0 0 6px
  font-size 11.5px
  font-weight 700
  letter-spacing .06em
  text-transform uppercase
  color CTRL_TEXT_MUTED

.big
  font-size 26px
  font-weight 700
  line-height 1.1
  color THEME_COLOR_PRIMARY

  small
    font-size 13px
    font-weight 600
    color CTRL_TEXT_MUTED

  &.bad
    color CTRL_ERROR

  &.warn
    color #b36b00

.sub
  margin 6px 0 0
  font-size 12px
  color CTRL_TEXT_MUTED

.warn
  font-weight 600
  color #b36b00

.bad
  font-weight 600
  color CTRL_ERROR

.meter
  display grid
  grid-template-columns 78px minmax(0, 1fr) auto
  align-items center
  gap 10px
  margin-bottom 5px
  font-size 12px
  color CTRL_TEXT_MUTED

.meter-label
  overflow hidden
  text-overflow ellipsis
  white-space nowrap

.meter-text
  font-weight 600
  color #3f3931
  text-align right
  white-space nowrap

.track
  height 6px
  overflow hidden
  background #eef1f6
  border-radius 3px

  i
    display block
    height 100%
    background #3fbd63
    transition width .3s

    &.warning
      background #ef9400

    &.critical
      background #ea4e2c

.pair
  display flex
  align-items baseline
  gap 10px
  margin-bottom 4px

  strong
    min-width 78px
    font-size 20px
    color THEME_COLOR_PRIMARY

  span
    font-size 12px
    color CTRL_TEXT_MUTED
</style>
